import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import Select from "react-select";
import {
  applyReferralApi,
  CreateAccountApi,
  fetchAirlinesApi,
  fetchHomeBasesApi,
  updateAccountApi,
  validateReferralCodeApi,
} from "apis/authServices";
import { auth } from "config/firebaseConfig";
import { useStore } from "store/zustand";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import TermsModal from "components/TermsModal";
import {
  clearSignupDraft,
  readSignupDraft,
  saveSignupDraft,
  SignupAccountPayload,
  SignupFormData,
  SIGNUP_TOTAL_STEPS,
  SKIP_IMAGE_VERIFICATION,
} from "./signupDraft";
import RegisterBg from "../../assets/images/Register.png";
import StandbyIcon from "../../assets/images/standby.png";

interface Airline {
  id: number;
  airlineName: string;
  logo: string | null;
}

interface Airport {
  id: number;
  name: string;
}

const EMPTY_FORM: SignupFormData = {
  firstName: "",
  lastName: "",
  employerAirlineId: "",
  employerAirline: "",
  homebaseId: "",
  homebase: "",
  referralCode: "",
};

// True while the user hasn't filled in any profile field, so re-hydrating a
// draft can never overwrite something they've already started typing.
// Deliberately ignores `referralCode`: a referral link auto-fills it on mount,
// which would otherwise look like input and block the restore.
const isPristine = (form: SignupFormData) =>
  !form.firstName &&
  !form.lastName &&
  !form.employerAirlineId &&
  !form.homebaseId;

const CreateAccount: React.FC = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useStore();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [isLoadingAirlines, setIsLoadingAirlines] = useState(false);
  const [isLoadingAirports, setIsLoadingAirports] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string>("");
  const [referralCodeError, setReferralCodeError] = useState<string>("");

  const [formData, setFormData] = useState<SignupFormData>(EMPTY_FORM);

  // Coming back from step 3 (or reloading this page) shouldn't wipe the form.
  // Waits for Firebase to report who's signed in, since the draft is only
  // restored for the account that saved it.
  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      if (!user) return;
      const draft = readSignupDraft(user.uid);
      if (!draft) return;
      setFormData((prev) => (isPristine(prev) ? draft.formData : prev));
      setTermsAccepted((prev) => prev || Boolean(draft.termsAccepted));
    });
  }, []);

  useEffect(() => {
    if (formData.referralCode === "") {
      setReferralCodeError("");
    }
  }, [formData.referralCode]);
  // Auto-fill referral code from query parameter
  useEffect(() => {
    const referralCodeFromUrl = searchParams.get("referral");
    if (referralCodeFromUrl && !formData.referralCode) {
      setFormData((prev) => ({ ...prev, referralCode: referralCodeFromUrl }));
      validateReferralCodeApi(referralCodeFromUrl)
        .then(() => setReferralCodeError(""))
        .catch(() => setReferralCodeError("Invalid referral code "));
    }
  }, [searchParams]);

  // Fetch airlines and home bases on component mount
  useEffect(() => {
    // Load initial limited data
    fetchAirlines("", 50); // Limit initial load to 50 items
    fetchHomeBases("", 50); // Limit initial load to 50 items
  }, []);

  // Debounce function for search input
  const debounce = useCallback(
    (func: (...args: any[]) => void, delay: number) => {
      let timeoutId: NodeJS.Timeout;
      return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
      };
    },
    [],
  );

  const fetchAirlines = async (query = "", limit = 100) => {
    try {
      setIsLoadingAirlines(true);
      const response = await fetchAirlinesApi(query);
      const airlineData = Array.isArray(response) ? response : [];

      // Limit the number of items to improve performance
      const limitedData = airlineData.slice(0, limit);
      setAirlines(limitedData);
    } catch (error) {
      console.error("Error fetching airlines:", error);
      setAirlines([]);
    } finally {
      setIsLoadingAirlines(false);
    }
  };

  const fetchHomeBases = async (query = "", limit = 100) => {
    try {
      setIsLoadingAirports(true);
      const response = await fetchHomeBasesApi(query);
      const airportData = Array.isArray(response) ? response : [];

      // Limit the number of items to improve performance
      const limitedData = airportData.slice(0, limit);
      setAirports(limitedData);
    } catch (error) {
      console.error("Error fetching home bases:", error);
      setAirports([]);
    } finally {
      setIsLoadingAirports(false);
    }
  };

  // Create debounced search functions
  const debouncedFetchAirlines = useCallback(
    debounce((query: string) => fetchAirlines(query, 100), 300),
    [debounce],
  );

  const debouncedFetchHomeBases = useCallback(
    debounce((query: string) => fetchHomeBases(query, 100), 300),
    [debounce],
  );

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "referralCode" && formData.referralCode) {
      setReferralCodeError("");
      if (value && value.length > 1) {
        try {
          await validateReferralCodeApi(value);
          setReferralCodeError("");
        } catch (error) {
          setReferralCodeError("Invalid referral code");
        }
      }
    }
  };

  const handleSelectAirline = (selectedOption: any) => {
    setFormData((prev) => ({
      ...prev,
      employerAirlineId: selectedOption ? selectedOption.value : "",
      employerAirline: selectedOption ? selectedOption.label : "",
    }));

    // Selecting an option filters airlines; clearing it restores the default airline list.
    if (!selectedOption) {
      fetchAirlines("", 50);
    }
  };

  const handleSelectHomeBase = (selectedOption: any) => {
    setFormData((prev) => ({
      ...prev,
      homebaseId: selectedOption ? selectedOption.value : "",
      homebase: selectedOption ? selectedOption.label : "",
    }));

    // Selecting an option filters airports; clearing it restores the default airport list.
    if (!selectedOption) {
      fetchHomeBases("", 50);
    }
  };

  // Mirrors UploadProof's persistAccount: on a resumed signup the backend
  // record may not exist yet, so UpdateAccount 404s with "User not found" —
  // create the record, then retry the update.
  const persistAccount = async (accountPayload: SignupAccountPayload) => {
    const write = () =>
      updateAccountApi(
        accountPayload.firstName,
        accountPayload.lastName,
        accountPayload.employerAirlineId,
        accountPayload.homebaseId,
        accountPayload.referralCode ?? "",
      );

    try {
      await write();
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMsg =
        typeof err?.response?.data === "string"
          ? err.response.data
          : err?.response?.data?.message;
      const userMissing =
        status === 404 || /user not found/i.test(serverMsg || "");
      if (userMissing) {
        await CreateAccountApi();
        await write();
      } else {
        throw err;
      }
    }
  };

  // QA/dev path: skip step 3 (image verification) entirely and finish signup
  // right here.
  const createAccountWithoutVerification = async (
    accountPayload: SignupAccountPayload,
  ) => {
    showLoader("Setting up your account...");
    try {
      await persistAccount(accountPayload);

      const referralCode = accountPayload.referralCode?.trim();
      if (referralCode) {
        try {
          await applyReferralApi(referralCode);
        } catch (referralError: any) {
          console.error("Failed to apply referral:", referralError);
          const message: string = referralError?.message || "";
          if (message.toLowerCase().includes("already")) {
            sessionStorage.setItem("referralMessage", message);
          }
        }
      }

      clearSignupDraft();
      setIsAuthenticated(true);
      navigate("/");
    } catch (error: any) {
      console.error("Error creating account:", error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.title;
      setVerificationMessage(
        apiMessage || "We couldn't create your account. Please try again.",
      );
    } finally {
      hideLoader();
    }
  };

  // Step 2 validates the profile fields, then either hands them to step 3
  // (UploadProof), which persists the account once the proof photo passes
  // verification, or — in QA/dev — persists the account directly, skipping
  // image verification.
  const handleSubmit = () => {
    if (!formData.firstName.trim()) {
      setVerificationMessage("Please enter your first name.");
      return;
    }

    if (!formData.lastName.trim()) {
      setVerificationMessage("Please enter your last name.");
      return;
    }

    if (!formData.employerAirlineId) {
      setVerificationMessage("Please select your employer airline.");
      return;
    }

    if (!formData.homebaseId) {
      setVerificationMessage("Please select your home base.");
      return;
    }

    if (!termsAccepted) {
      setVerificationMessage("Terms and conditions must be accepted.");
      return;
    }

    if (referralCodeError) {
      setVerificationMessage(
        "Please enter a valid referral code or leave it empty.",
      );
      return;
    }

    setVerificationMessage("");
    const accountPayload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      employerAirlineId: Number(formData.employerAirlineId),
      homebaseId: Number(formData.homebaseId),
      referralCode: formData.referralCode?.trim() ?? "",
    };
    // Stashed so step 3 survives a refresh, and so coming back here restores
    // everything the user typed instead of handing them an empty form.
    const uid = auth.currentUser?.uid;
    if (uid) {
      saveSignupDraft({ uid, termsAccepted, formData, accountPayload });
    }

    if (SKIP_IMAGE_VERIFICATION) {
      createAccountWithoutVerification(accountPayload);
      return;
    }

    navigate("/uploadProof", { state: { accountPayload } });
  };

  // Transform airlines and airports into react-select options with memoization
  const airlineOptions = useMemo(
    () =>
      airlines.map((airline) => ({
        value: airline.id.toString(),
        label: airline.airlineName,
      })),
    [airlines],
  );

  const airportOptions = useMemo(
    () =>
      airports.map((airport) => ({
        value: airport.id.toString(),
        label: airport.name,
      })),
    [airports],
  );

  // Find selected values for react-select with fallback (memoized)
  const selectedAirline = useMemo(
    () =>
      formData.employerAirlineId
        ? airlineOptions.find(
            (option) => option.value === formData.employerAirlineId,
          ) || {
            value: formData.employerAirlineId,
            label: formData.employerAirline || formData.employerAirlineId,
          }
        : null,
    [formData.employerAirlineId, formData.employerAirline, airlineOptions],
  );

  const selectedHomeBase = useMemo(
    () =>
      formData.homebaseId
        ? airportOptions.find(
            (option) => option.value === formData.homebaseId,
          ) || {
            value: formData.homebaseId,
            label: formData.homebase || formData.homebaseId,
          }
        : null,
    [formData.homebaseId, formData.homebase, airportOptions],
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50 }}>
      {/* Full-page Background Image */}
      <img
        src={RegisterBg}
        alt="Register background"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.35)",
        }}
      />

      {/* Content Layer */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
          height: "100%",
          overflowY: isMobile ? "auto" : "hidden",
        }}
      >
        {/* Left - Logo & Tagline */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: isMobile ? "1.5rem" : "2.5rem",
          }}
        >
          <div className="flex items-center gap-2">
            <img src={StandbyIcon} alt="Standby" className="w-8 h-8" />
            <span className="text-white text-xl font-bold tracking-wide">
              STANDBY
            </span>
          </div>
          {!isMobile && (
            <div className="mb-10">
              <h1 className="text-white text-4xl font-bold leading-tight">
                Connecting <span className="text-light-yellow">Air Crews,</span>
                <br />
                <span className="italic">Seamlessly.</span>
              </h1>
            </div>
          )}
        </div>

        {/* Right - Form Card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "1rem" : "2rem",
            minWidth: isMobile ? 0 : "640px",
            maxWidth: isMobile ? "100%" : "720px",
            width: isMobile ? "100%" : undefined,
          }}
        >
          <div
            className="w-full"
            style={{
              maxWidth: isMobile ? "28rem" : undefined,
              marginLeft: isMobile ? "auto" : undefined,
              marginRight: isMobile ? "auto" : undefined,
            }}
          >
            <div
              className={
                isMobile
                  ? "bg-white rounded-2xl shadow-lg px-6 py-10 overflow-y-auto"
                  : "bg-white rounded-2xl shadow-lg px-12 py-12 sm:px-14 sm:py-14 overflow-y-auto"
              }
              style={{ maxHeight: isMobile ? "none" : "95vh" }}
            >
              <div className="flex justify-center">
                <span className="inline-block px-4 py-1 rounded-full border border-gray-200 text-xs text-grease-100">
                  Step 2 of {SIGNUP_TOTAL_STEPS}
                </span>
              </div>
              <h2 className="text-2xl font-bold text-grease-100 text-center mt-4">
                Create An Account
              </h2>
              {verificationMessage && (
                <p className="mt-4 text-sm text-red-700 bg-red-50 p-3 rounded-lg">
                  {verificationMessage}
                </p>
              )}

              <div
                className={`mt-6 flex gap-4 ${
                  isMobile ? "flex-col items-stretch" : "flex-row items-center"
                }`}
              >
                <div className="w-full">
                  <label className="block text-sm font-medium text-grease-100 mb-2">
                    First Name
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    id="firstName"
                    maxLength={50}
                    className="block w-full p-3.5 rounded-lg bg-white text-sm text-gray-800
                      focus:outline-none focus:ring-1 focus:ring-light-yellow
                      border border-gray-200 focus:border-light-yellow"
                    placeholder="Enter First Name"
                  />
                </div>
                <div className="w-full">
                  <label className="block text-sm font-medium text-grease-100 mb-2">
                    Last Name
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    id="lastName"
                    maxLength={50}
                    className="block w-full p-3.5 rounded-lg bg-white text-sm text-gray-800
                      focus:outline-none focus:ring-1 focus:ring-light-yellow
                      border border-gray-200 focus:border-light-yellow"
                    placeholder="Enter Last Name"
                  />
                </div>
              </div>

              <div className="mt-4 relative">
                <label className="block text-sm font-medium text-grease-100 mb-2">
                  Your Employer Airline
                </label>
                <i
                  className="st-flight text-base absolute left-4 top-[44px] z-10 pointer-events-none inline-block"
                  style={{
                    transform: "rotate(-50deg)",
                    color: "transparent",
                    WebkitTextStroke: "0.75px #868686",
                  }}
                ></i>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  value={selectedAirline}
                  isLoading={isLoadingAirlines}
                  isClearable={true}
                  isSearchable={true}
                  name="employerAirlineId"
                  options={airlineOptions}
                  onChange={handleSelectAirline}
                  onInputChange={(inputValue, { action }) => {
                    if (
                      action === "input-change" &&
                      inputValue.trim().length > 1
                    ) {
                      debouncedFetchAirlines(inputValue);
                    }
                  }}
                  placeholder={
                    <span>
                      Select Employer Airline{" "}
                      <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  noOptionsMessage={({ inputValue }) =>
                    isLoadingAirlines
                      ? "Loading..."
                      : inputValue
                        ? `No airlines found for "${inputValue}"`
                        : "Type to search airlines"
                  }
                  maxMenuHeight={200}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      outline: "none !important",
                      backgroundColor: "#ffffff",
                      border: state.isFocused
                        ? "1px solid #FFBC00"
                        : "1px solid #e5e7eb",
                      padding: "0.35rem",
                      paddingLeft: "1.75rem",
                      borderRadius: "0.5rem",
                      boxShadow: state.isFocused ? "0 0 0 1px #FFBC00" : "none",
                      "&:hover": {
                        borderColor: state.isFocused ? "#FFBC00" : "#CBD5E0",
                      },
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#A0AEC0",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#2D3748",
                    }),
                    option: (base, state) => ({
                      ...base,
                      color: "#2D3748",
                      backgroundColor: state.isSelected
                        ? "#FCD34D"
                        : state.isFocused
                          ? "#FDE68A"
                          : "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#FDE68A",
                      },
                    }),
                  }}
                  filterOption={null}
                />
                {airlines.length === 0 && !isLoadingAirlines && (
                  <p className="text-sm text-gray-500 mt-1">
                    Start typing to search airlines
                  </p>
                )}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-grease-100 mb-2">
                  Your HomeBase
                </label>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  value={selectedHomeBase}
                  isLoading={isLoadingAirports}
                  isClearable={true}
                  isSearchable={true}
                  name="homebaseId"
                  options={airportOptions}
                  onChange={handleSelectHomeBase}
                  onInputChange={(inputValue, { action }) => {
                    if (
                      action === "input-change" &&
                      inputValue.trim().length > 1
                    ) {
                      debouncedFetchHomeBases(inputValue);
                    }
                  }}
                  placeholder={
                    <span>
                      Enter Your HomeBase{" "}
                      <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  noOptionsMessage={({ inputValue }) =>
                    isLoadingAirports
                      ? "Loading..."
                      : inputValue
                        ? `No home bases found for "${inputValue}"`
                        : "Type to search home bases"
                  }
                  maxMenuHeight={200}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      outline: "none !important",
                      backgroundColor: "#ffffff",
                      border: state.isFocused
                        ? "1px solid #FFBC00"
                        : "1px solid #e5e7eb",
                      padding: "0.35rem",
                      borderRadius: "0.5rem",
                      boxShadow: state.isFocused ? "0 0 0 1px #FFBC00" : "none",
                      "&:hover": {
                        borderColor: state.isFocused ? "#FFBC00" : "#CBD5E0",
                      },
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#A0AEC0",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#2D3748",
                    }),
                    option: (base, state) => ({
                      ...base,
                      color: "#2D3748",
                      backgroundColor: state.isSelected
                        ? "#FCD34D"
                        : state.isFocused
                          ? "#FDE68A"
                          : "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#FDE68A",
                      },
                    }),
                    input: (base) => ({
                      ...base,
                      outline: "none !important",
                      color: "#2D3748",
                      "& input": {
                        outline: "none !important",
                        boxShadow: "none !important",
                        border: "none !important",
                      },
                    }),
                  }}
                  filterOption={null}
                />
                {airports.length === 0 && !isLoadingAirports && (
                  <p className="text-sm text-gray-500 mt-1">
                    Start typing to search home bases
                  </p>
                )}
              </div>

              <div className="mt-4">
                <input
                  type="text"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleInputChange}
                  maxLength={32}
                  className="block w-full p-3.5 rounded-lg bg-white text-sm text-gray-800 placeholder-gray-400
                    border border-gray-200
                    focus:outline-none
                    focus:ring-1
                    focus:ring-light-yellow
                    focus:border-light-yellow"
                  placeholder="Referral Code (Optional)"
                />
                {referralCodeError && (
                  <p className="mt-2 text-sm text-red-600">
                    {referralCodeError}
                  </p>
                )}
              </div>

              <div className="flex items-center mt-5">
                <input
                  id="checked-checkbox"
                  type="checkbox"
                  checked={termsAccepted}
                  onClick={(e) => {
                    if (!termsAccepted) {
                      e.preventDefault();
                      setIsTermsModalOpen(true);
                    }
                  }}
                  onChange={(e) => {
                    if (termsAccepted) {
                      setTermsAccepted(e.target.checked);
                    }
                  }}
                  className="w-4 h-4 text-light-yellow bg-gray-100 border-gray-300 rounded focus:ring-light-yellow cursor-pointer"
                />
                <label
                  htmlFor="checked-checkbox"
                  className="ml-2 text-xs font-medium text-grease-100"
                >
                  I accept the{" "}
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      setIsTermsModalOpen(true);
                    }}
                    className="underline text-light-yellow hover:text-yellow-600 cursor-pointer"
                  >
                    terms & conditions
                  </a>
                </label>
              </div>
              <TermsModal
                isOpen={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
                onAccept={() => {
                  setTermsAccepted(true);
                  setIsTermsModalOpen(false);
                }}
              />
              <button
                onClick={handleSubmit}
                disabled={!termsAccepted}
                className="w-full p-3.5 rounded-lg text-[#192227] text-center font-semibold shadow-button bg-light-yellow text-sm mt-5 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
